import { useRef, useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '~/utils';

type DragAndDropInputProps = {
  accept?: string;
  maxSizeMB?: number;
  onChange: (file: File) => void;
  onRemove?: () => void;
  onError?: (message: string) => void;
  file?: File | null;
};

export function DragAndDropInput({
  accept,
  maxSizeMB = 5,
  onChange,
  onRemove,
  onError,
  file,
}: DragAndDropInputProps) {
  const { t } = useTranslation('general');
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isTypeAccepted = (file: File): boolean => {
    if (!accept) return true;
    const tokens = accept.split(',').map((s) => s.trim().toLowerCase());
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const mime = file.type.toLowerCase();
    return tokens.some((token) => {
      if (token.startsWith('.')) return ext === token;
      if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
      return mime === token;
    });
  };

  const validate = (selected: File): boolean => {
    if (!isTypeAccepted(selected)) {
      onError?.(t('drag_and_drop.type_error', { accept }));
      return false;
    }
    if (selected.size > maxSizeMB * 1024 * 1024) {
      onError?.(t('drag_and_drop.size_error', { maxSizeMB }));
      return false;
    }
    return true;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && validate(dropped)) onChange(dropped);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected && validate(selected)) onChange(selected);
    e.target.value = '';
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'w-full rounded-lg border-2 border-dashed p-8 text-center transition-colors',
        isDragging ? 'border-prim' : 'border-neut-300 hover:border-prim'
      )}
    >
      <Upload
        size={48}
        strokeWidth={2}
        className="mx-auto mb-4 text-neut-400"
      />

      {file ? (
        <>
          <div className="flex items-center justify-center w-full gap-3">
            <FileText size={16} className="text-neut-prim" />
            <p className="text-sm font-medium text-neut-prim">{file.name}</p>
            <p className="text-xs text-neut-sec mt-1">
              ({(file.size / 1024).toFixed(1)} KB)
            </p>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-error-600 underline underline-offset-2 cursor-pointer bg-transparent border-none p-0"
          >
            {t('drag_and_drop.remove_file')}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-neut-700 mb-2">
            {t('drag_and_drop.hint')}
          </p>
          <label className="inline-block">
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={handleFileChange}
            />
            <span className="px-4 py-2 bg-prim text-neut-0 rounded-lg hover:bg-prim-hover transition-colors text-sm font-medium cursor-pointer inline-block">
              {t('drag_and_drop.browse_files')}
            </span>
          </label>
        </>
      )}

      {file ? null : (
        <p className="text-xs text-neut-500 mt-3">
          {t('drag_and_drop.accepted_format', {
            accept: accept ?? 'any',
            maxSizeMB,
          })}
        </p>
      )}
    </div>
  );
}
